import { useEffect, useRef, useState, type ReactNode } from "react";
import { Dialogo } from "./Dialogo";

/** Os cortes da casca da area (spec 038): celular abaixo de 600 px, trilho de 600 a 1023, gaveta de 1024 pra cima. */
export const CELULAR_ATE = "(max-width: 599px)";
export const GAVETA_DESDE = "(min-width: 1024px)";

/** Se a consulta de midia vale agora; acompanha girar o aparelho e redimensionar a janela. */
export function useMidia(consulta: string): boolean {
  const [vale, setVale] = useState(() => typeof window !== "undefined" && window.matchMedia(consulta).matches);
  useEffect(() => {
    const m = window.matchMedia(consulta);
    const mudou = () => setVale(m.matches);
    mudou();
    m.addEventListener("change", mudou);
    return () => m.removeEventListener("change", mudou);
  }, [consulta]);
  return vale;
}

/**
 * O que abre a partir de um botao da casca (sino, conta, seletor de plano). No celular e a folha inferior do `Dialogo`
 * (fundo escurecido, Fechar, rolagem do fundo travada). Da largura do trilho pra cima e um painel ancorado no botao,
 * com um veu invisivel por baixo: tocar fora so fecha, o toque nao atravessa pro que esta embaixo (spec 038, 1.3).
 * Fecha no Esc e devolve o foco a quem abriu. `acao` fica ao lado do titulo (ex.: "Marcar todas como lidas").
 * `posicao` diz pra onde o painel cresce a partir do botao; a casca troca isso por CSS no trilho.
 */
export function Flutuante({ aberto, aoFechar, titulo, descricao, acao, posicao = "abaixo-esquerda", classe, children }: { aberto: boolean; aoFechar: () => void; titulo: string; descricao?: ReactNode; acao?: ReactNode; posicao?: "abaixo-esquerda" | "abaixo-direita"; classe?: string; children: ReactNode }) {
  const folha = useMidia(CELULAR_ATE);
  const caixa = useRef<HTMLDivElement>(null);
  const fechar = useRef(aoFechar);
  fechar.current = aoFechar;
  useEffect(() => {
    if (!aberto || folha) return;
    const antes = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    caixa.current?.focus({ preventScroll: true });
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); fechar.current(); } };
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("keydown", esc);
      // Foco de volta no botao que abriu, menos quando o fechamento levou pra outra tela (o foco ja foi pra la).
      if (antes?.isConnected && (!document.activeElement || document.activeElement === document.body || caixa.current?.contains(document.activeElement))) antes.focus({ preventScroll: true });
    };
  }, [aberto, folha]);

  if (folha) {
    return (
      <Dialogo aberto={aberto} titulo={titulo} descricao={descricao} aoFechar={aoFechar}>
        <div className={`mtc-flutuante__corpo${classe ? ` ${classe}` : ""}`}>
          {acao && <div className="mtc-flutuante__acao">{acao}</div>}
          {children}
        </div>
      </Dialogo>
    );
  }
  if (!aberto) return null;
  return (
    <>
      <div className="mtc-veu" aria-hidden="true" onClick={aoFechar} />
      <div ref={caixa} className={`mtc-flutuante mtc-flutuante--${posicao}${classe ? ` ${classe}` : ""}`} role="dialog" aria-label={titulo} tabIndex={-1}>
        <div className="mtc-flutuante__cabeca">
          <div className="mtc-flutuante__textos">
            <strong>{titulo}</strong>
            {descricao && <span className="mtc-flutuante__descricao">{descricao}</span>}
          </div>
          {acao}
        </div>
        <div className="mtc-flutuante__corpo">{children}</div>
      </div>
    </>
  );
}
